/**
 * Payment Launcher Utility
 * Builds Deep Link URIs for Smart POS and payment terminals
 */

export type TerminalProvider = 'stone' | 'mercadopago' | 'pagseguro' | 'cielo';

export interface PaymentLaunchParams {
  provider: TerminalProvider;
  amount: number; // em reais (será convertido para centavos quando necessário)
  transactionId: string;
  paymentMethod: 'credit' | 'debit';
  installments?: number;
  description?: string;
  returnScheme?: string;
}

export interface PaymentLaunchResult {
  success: boolean;
  uri?: string;
  error?: string;
}

// Provider Deep Link schemes
const PROVIDER_SCHEMES: Record<TerminalProvider, string> = {
  stone: 'stone://payment',
  mercadopago: 'https://www.mercadopago.com/point/integrations',
  pagseguro: 'pagseguro://payment',
  cielo: 'cielomobile://payment',
};

// Convert amount to centavos (integer)
function toCentavos(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * Builds a payment URI for the specified provider
 */
export function buildPaymentUri(params: PaymentLaunchParams): PaymentLaunchResult {
  const {
    provider,
    amount,
    transactionId,
    paymentMethod,
    installments = 1,
    description,
    returnScheme = 'gonx',
  } = params;

  if (amount <= 0) {
    return { success: false, error: 'Valor deve ser maior que zero' };
  }

  if (!transactionId) {
    return { success: false, error: 'ID da transação é obrigatório' };
  }

  try {
    let uri: string;
    const amountCentavos = toCentavos(amount);

    switch (provider) {
      case 'stone':
        // Stone Smart POS Deep Link
        // stone://payment?amount={centavos}&editable_amount=0&transaction_id={uuid}&return_scheme=gonx&return_url=gonx://payment-callback&payment_type={1=credit,2=debit}&installments={n}
        const paymentType = paymentMethod === 'credit' ? 1 : 2;
        // Incluir return_url para Capacitor nativo receber o callback
        uri = `stone://payment?amount=${amountCentavos}&editable_amount=0&transaction_id=${transactionId}&return_scheme=${returnScheme}&return_url=${encodeURIComponent('gonx://payment-callback')}&payment_type=${paymentType}`;
        if (paymentMethod === 'credit' && installments > 1) {
          uri += `&installments=${installments}`;
        }
        break;

      case 'mercadopago':
        // Mercado Pago Point Deep Link
        // Uses HTTPS for universal links
        const mpDescription = description || `Venda_${transactionId.slice(0, 8)}`;
        uri = `https://www.mercadopago.com/point/integrations?amount=${amountCentavos}&description=${encodeURIComponent(mpDescription)}&external_reference=${transactionId}`;
        break;

      case 'pagseguro':
        // PagSeguro PlugPag Deep Link
        const pgPaymentMethod = paymentMethod === 'credit' ? 'CREDITO' : 'DEBITO';
        uri = `pagseguro://payment?amount=${amountCentavos}&reference=${transactionId}&payment_method=${pgPaymentMethod}`;
        if (paymentMethod === 'credit' && installments > 1) {
          uri += `&installments=${installments}`;
        }
        break;

      case 'cielo':
        // Cielo Mobile Deep Link
        const cieloPaymentType = paymentMethod === 'credit' ? 'CREDITO' : 'DEBITO';
        uri = `cielomobile://payment?amount=${amountCentavos}&orderId=${transactionId}&paymentType=${cieloPaymentType}`;
        if (paymentMethod === 'credit' && installments > 1) {
          uri += `&installments=${installments}`;
        }
        break;

      default:
        return { success: false, error: `Provider não suportado: ${provider}` };
    }

    return { success: true, uri };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro ao construir URI de pagamento' 
    };
  }
}

/**
 * Get the scheme for a provider
 */
export function getProviderScheme(provider: TerminalProvider): string {
  return PROVIDER_SCHEMES[provider];
}

/**
 * Launch payment app via Deep Link
 */
export function launchPaymentApp(params: PaymentLaunchParams): PaymentLaunchResult {
  const result = buildPaymentUri(params);
  
  if (!result.success || !result.uri) {
    return result;
  }

  try {
    // Navigate to the payment app
    window.location.href = result.uri;
    return { success: true, uri: result.uri };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao abrir app de pagamento',
    };
  }
}

/**
 * Check if the current device might support payment apps
 */
export function canUseDeepLinks(): boolean {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check if it's a mobile device or known Smart POS
  const isMobile = /android|iphone|ipad|ipod|mobile/i.test(userAgent);
  const isSmartPOS = /sunmi|pax|ingenico|verifone|stone|point|moderninha|plugpag/i.test(userAgent);
  
  return isMobile || isSmartPOS;
}

/**
 * Detect if running on a known Smart POS terminal
 */
export function detectSmartPOSProvider(): TerminalProvider | null {
  if (typeof window === 'undefined') return null;
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Stone terminals (Sunmi, PAX with Stone app)
  if (/sunmi|stonepago/i.test(userAgent)) {
    return 'stone';
  }
  
  // Mercado Pago Point terminals
  if (/point|pointpro|mercadopago/i.test(userAgent)) {
    return 'mercadopago';
  }
  
  // PagSeguro terminals (Moderninha)
  if (/moderninha|plugpag|pagseguro/i.test(userAgent)) {
    return 'pagseguro';
  }
  
  // Cielo terminals
  if (/cielo|lio/i.test(userAgent)) {
    return 'cielo';
  }
  
  return null;
}

/**
 * Get provider display info
 */
export function getProviderInfo(provider: TerminalProvider): { name: string; color: string } {
  const providers: Record<TerminalProvider, { name: string; color: string }> = {
    stone: { name: 'Stone', color: 'bg-emerald-500' },
    mercadopago: { name: 'Mercado Pago', color: 'bg-sky-500' },
    pagseguro: { name: 'PagSeguro', color: 'bg-green-500' },
    cielo: { name: 'Cielo', color: 'bg-blue-500' },
  };
  
  return providers[provider];
}

/**
 * Generate a unique transaction ID for Deep Link tracking
 */
export function generateTransactionId(): string {
  // Format: DLPAY-{timestamp}-{random}
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `DLPAY-${timestamp}-${random}`.toUpperCase();
}
